// src/merch/guides.js
//
// Colouring guides + the helmet face-opening shape.
//
// Guides are light grey, dashed, NON-PRINTING lines that show kids where
// the helmet, face, gloves, jersey hem, pants/socks and crest go. They're
// drawn on the colouring sheet and faintly in the editor, never on the
// sticker. (On a scanned sheet they're as light as the trim line, so the
// clean-up whitens them.)
//
// All coordinates are mm inside the sticker's trim box (same space as the
// zone outline). Measured from Shep's sketch on the ornament front.

import ornamentTemplate from "./models/ornament.template.json";
import ornamentGuides from "./models/ornament.guides.json";

/** Helmet face opening, traced from Shep's helmet drawing: flat top
 *  under the brim with rounded corners, straight sides, round chin
 *  (almost as wide as tall). Returns points for a box (x, y, w, h). */
export function faceShapePoints(x, y, w, h, steps = 20) {
  const pts = [];
  const r = w * 0.1; // rounded top corners
  const q = (p0, p1, p2, t) => { const u = 1 - t; return [u * u * p0[0] + 2 * u * t * p1[0] + t * t * p2[0], u * u * p0[1] + 2 * u * t * p1[1] + t * t * p2[1]]; };
  const bez = (p0, p1, p2, p3, t) => {
    const u = 1 - t;
    return [
      u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
      u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
    ];
  };
  const n = 6;
  // top edge + top-right corner
  pts.push([x + r, y], [x + w - r, y]);
  for (let i = 1; i <= n; i++) pts.push(q([x + w - r, y], [x + w, y], [x + w, y + r], i / n));
  // straight right side
  pts.push([x + w, y + h * 0.55]);
  // round chin, right half then left half
  const R0 = [x + w, y + h * 0.55], R1 = [x + w, y + h * 0.82], R2 = [x + w * 0.72, y + h], R3 = [x + w * 0.5, y + h];
  for (let i = 1; i <= steps; i++) pts.push(bez(R0, R1, R2, R3, i / steps));
  const L0 = [x + w * 0.5, y + h], L1 = [x + w * 0.28, y + h], L2 = [x, y + h * 0.82], L3 = [x, y + h * 0.55];
  for (let i = 1; i <= steps; i++) pts.push(bez(L0, L1, L2, L3, i / steps));
  // straight left side + top-left corner
  pts.push([x, y + r]);
  for (let i = 1; i < n; i++) pts.push(q([x, y + r], [x, y], [x + r, y], i / n));
  return pts;
}

/** Canvas path of the face opening in a box. */
export function faceShapePath(ctx, x, y, w, h) {
  const p = faceShapePoints(x, y, w, h);
  ctx.beginPath();
  p.forEach(([px, py], i) => (i ? ctx.lineTo(px, py) : ctx.moveTo(px, py)));
  ctx.closePath();
}

// half-ellipse / arc helper → polyline
function arc(cx, cy, rx, ry, a0, a1, n = 20) {
  const out = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    out.push([cx + rx * Math.cos(a), cy + ry * Math.sin(a)]);
  }
  return out;
}

// ---- ornament (player figure, ~49.6 × 79 mm) ----
// face hole: measured from the jersey art by tools/merch/prep_template.py
// (re-run it after changing the art and this follows automatically)
const FRONT_TPL = ornamentTemplate.sides?.["ornament-front"] || {};
// helmet-on-top art marks a face box (white on the helmet map); older
// cut-out art has a face hole instead
const HELMET_MODE = !!(FRONT_TPL.helmet && FRONT_TPL.faceBoxMm);
const FACE = FRONT_TPL.faceBoxMm || FRONT_TPL.faceHoleMm || { x: 20.38, y: 23.31, w: 8.81, h: 9.25 };
const W = 49.59;

const ornamentFront = [
  // helmet brim (the cut line is the helmet dome)
  { pts: [[19.4, 24.2], [30.2, 24.2]] },
  // face opening (same shape the face photo is cropped to)
  { pts: faceShapePoints(FACE.x, FACE.y, FACE.w, FACE.h), closed: true },
  // glove cuffs
  { pts: [[0.5, 16.8], [10.5, 13.4]] },
  { pts: [[39.8, 14.4], [49.4, 17.3]] },
  // chest crest
  { pts: arc(23.9, 44.1, 4.6, 4.6, 0, 2 * Math.PI, 36), closed: true },
  // jersey hem
  { pts: [[9.5, 55.2], [17, 56.2], [25, 56.9], [33, 57.3], [41, 57.6]] },
  // pants → socks
  { pts: [[10, 73.2], [45, 73.2]] },
];

// back: mirror of the front, solid helmet (no face), name + number boxes
const mirror = (g) => ({ ...g, pts: g.pts.map(([x, y]) => [+(W - x).toFixed(2), y]) });
const ornamentBack = [
  { pts: [[19.4, 24.2], [19.6, 30.4], [22, 33.2], [27.6, 33.2], [30, 30.4], [30.2, 24.2]] },
  { pts: [[0.5, 16.8], [10.5, 13.4]] },
  { pts: [[39.8, 14.4], [49.4, 17.3]] },
  { pts: [[9.5, 55.2], [17, 56.2], [25, 56.9], [33, 57.3], [41, 57.6]] },
  { pts: [[10, 73.2], [45, 73.2]] },
  // name + number areas (dotted boxes)
  { pts: [[14, 36.2], [35.6, 36.2], [35.6, 41.4], [14, 41.4]], closed: true, dot: true },
  { pts: [[17, 42.8], [32.6, 42.8], [32.6, 53.4], [17, 53.4]], closed: true, dot: true },
].map(mirror);

// Colouring guides drawn by Shep (from his colouring-sheet PDFs, measured
// to the true dieline): glove cuffs, face / helmet line and jersey hem.
export const GUIDES = {
  "ornament-front": ornamentGuides["ornament-front"] || ornamentFront,
  "ornament-back": ornamentGuides["ornament-back"] || ornamentBack,
};

/** Face spot for products: centre + width as fractions of the sticker. */
export const ORNAMENT_FACE = HELMET_MODE
  ? {
      // helmet sits ON the face: keep the face's own outline (face-only
      // cut-out), framed to the face box, with room for cheeks / chin
      x: (FACE.x + FACE.w / 2) / W,
      y: (FACE.y + FACE.h / 2) / 79.05,
      w: FACE.w / W,
      aspect: FACE.w / FACE.h,
      shape: "free",
      pad: 1.4,
    }
  : {
      x: (FACE.x + FACE.w / 2) / W,
      y: (FACE.y + FACE.h / 2) / 79.05,
      // a touch bigger than the hole: the jersey art sits on top and frames it
      w: (FACE.w * 1.12) / W,
      aspect: FACE.w / FACE.h,
      shape: "helmet",
    };
